import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  denetimEkrani,
  kotaDurumu,
  pilotGostergeleri,
  type DenetimSatiri,
  type KotaDurumu,
  type PilotGostergeleri,
} from '../api/puantaj';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { HataKutusu, Sekme } from '../bilesenler/temel';
import { bugun, gunEkle, saatMetni, sureMetni, tarihMetni } from '../bicim/tarih';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';

/**
 * PUANTAJ DENETİMİ.
 *
 * Demo hattının "çıktı" adımı. BU BİR ONAY KUYRUĞU DEĞİLDİR: şef mesai onayı
 * vermez, yalnız bakması gereken günleri görür. Onay zorunlu olsaydı 150
 * kişilik pilotta şef üçüncü haftada hepsini toplu geçmeye başlardı ve onay
 * adımı hiçbir denetim işlevi görmezdi.
 *
 * Sunucu yalnız İŞARETLİ, DEVAMSIZ, ÖN İZİNSİZ ACİL ya da DÜZELTİLMİŞ günleri
 * döndürüyor; tüm günler gelseydi dikkat çekmesi gereken beş satır kaybolurdu.
 */
const VARSAYILAN_GUN = 14;

export function PuantajDenetimi() {
  /*
   * SEKME YETKİSİ UÇLARLA HİZALI.
   *
   * Menü iki sekmenin BİRLEŞİMİYLE açılıyor ama uçların listesi farklı:
   * `/puantaj/denetim` → `DENETIM_EKRANI`,
   * `/puantaj/pilot-gostergeleri` → `DENETIM_EKRANI | RAPOR_GORUNTULE`,
   * `/puantaj/kota` → `DENETIM_EKRANI | MESAI_ON_IZIN_ONAY | RAPOR_GORUNTULE`.
   *
   * Yalnız rapor yetkisi olan bir kullanıcı (örn. İK) denetim sekmesini
   * açsaydı 403 alır ve sebebini anlamazdı; yalnız `MESAI_ON_IZIN_ONAY`
   * olan bir şef ise kota sekmesini hiç göremezdi. Her sekme kendi ucunun
   * listesine bakıyor.
   */
  const denetimGorebilir = yetkiKullan('DENETIM_EKRANI');
  const raporGorebilir = yetkiKullan('DENETIM_EKRANI', 'RAPOR_GORUNTULE');
  const kotaGorebilir = yetkiKullan('DENETIM_EKRANI', 'MESAI_ON_IZIN_ONAY', 'RAPOR_GORUNTULE');
  const yetkilerHazir = yetkilerHazirKullan();

  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [baslangic, baslangicYaz] = useState(() => gunEkle(bugun(), -VARSAYILAN_GUN));
  const [bitis, bitisYaz] = useState(() => bugun());

  /*
   * SEKME TÜRETİLİR, BAŞLANGIÇ DEĞERİ OLARAK DONDURULMAZ.
   *
   * `useState(denetimGorebilir ? … )` yazılsaydı değer İLK render'da
   * donardı; `/kimlik/ben` henüz dönmemişken `yetkiler` boş olduğu için
   * `DENETIM_EKRANI` sahibi kullanıcı bu ekrana doğrudan F5 ile girdiğinde
   * kota sekmesinde açılırdı. Kullanıcı seçim yapana kadar (`sekmeSecimi`
   * null) yetkiden türetilir.
   */
  const [sekmeSecimi, sekmeSecimiYaz] = useState<'denetim' | 'kota' | null>(null);
  const sekme: 'denetim' | 'kota' = sekmeSecimi ?? (denetimGorebilir ? 'denetim' : 'kota');

  const sorgu = { birimId, baslangic, bitis };

  /*
   * SORGULAR YETKİLER GELMEDEN AÇILMAZ.
   *
   * `yetkilerHazir` beklenmezse ilk render'da yetkiler boş olur, koşul
   * `false` çıkar ve TanStack sorguyu hiç kurmaz; yetkiler geldiğinde
   * `enabled` true olur ve istek gecikmeli gider. Bayrakla beklemek bunu
   * tek adıma indirir ve yetkisiz kullanıcıda hiç istek üretmez.
   */
  const denetim = useQuery({
    queryKey: ['puantaj', 'denetim', sorgu],
    queryFn: () => denetimEkrani(sorgu),
    enabled: yetkilerHazir && sekme === 'denetim' && denetimGorebilir,
  });

  const gosterge = useQuery({
    queryKey: ['puantaj', 'gosterge', sorgu],
    queryFn: () => pilotGostergeleri(sorgu),
    enabled: yetkilerHazir && sekme === 'denetim' && raporGorebilir,
  });

  const kota = useQuery({
    queryKey: ['puantaj', 'kota', birimId],
    queryFn: () => kotaDurumu(birimId),
    enabled: yetkilerHazir && sekme === 'kota' && kotaGorebilir,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Puantaj denetimi</h1>
        <p className="mt-1 text-sm text-slate-600">
          Yalnız bakılması gereken günler listelenir — işaretli, devamsız, ön izinsiz acil ya da
          düzeltilmiş. Eylem zorunlu değildir.
        </p>
      </div>

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <BirimSecici deger={birimId} degisti={birimIdYaz} bosSecenek="Kapsamımdaki tüm birimler" />

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Başlangıç</span>
          <input
            type="date"
            value={baslangic}
            max={bitis}
            onChange={(olay) => olay.target.value && baslangicYaz(olay.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Bitiş</span>
          <input
            type="date"
            value={bitis}
            min={baslangic}
            onChange={(olay) => olay.target.value && bitisYaz(olay.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          />
        </label>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {denetimGorebilir && (
          <Sekme
            etkin={sekme === 'denetim'}
            tikla={() => sekmeSecimiYaz('denetim')}
            etiket="Denetim"
          />
        )}
        {kotaGorebilir && (
          <Sekme
            etkin={sekme === 'kota'}
            tikla={() => sekmeSecimiYaz('kota')}
            etiket="270 saat kotası"
          />
        )}
      </div>

      {!yetkilerHazir ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'denetim' && denetimGorebilir ? (
        <>
          <HataKutusu hata={denetim.error} />
          {gosterge.data && <Gostergeler veri={gosterge.data} />}
          <DenetimTablosu satirlar={denetim.data ?? []} bekliyor={denetim.isPending} />
        </>
      ) : kotaGorebilir ? (
        <>
          <HataKutusu hata={kota.error} />
          <KotaTablosu satirlar={kota.data ?? []} bekliyor={kota.isPending} />
        </>
      ) : (
        /* Menü bu ekranı yalnız iki sekmeden birine yetkisi olana açıyor;
           yine de sessiz boş tablo yerine sebebi yazılır. */
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Bu ekranı görüntüleme yetkiniz yok.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pilot göstergeleri                                                  */
/* ------------------------------------------------------------------ */

function Gostergeler({ veri }: { veri: PilotGostergeleri }) {
  const gerekceler = Object.entries(veri.duzeltmeGerekceleri).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">Pilot göstergeleri</h2>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/*
          İKİ ORAN AYRI KUTUDA. Tek orana indirgenirse "işaret az" ile
          "şef bakmıyor" ayırt edilemez: ham işaretli oran şefin
          davranışından bağımsız, düzeltilen oran doğrudan ona bağlı.
        */}
        <Kutu
          etiket="Ham işaretli"
          deger={`%${veri.hamIsaretliOran}`}
          alt={`${veri.hamIsaretliGun} / ${veri.toplamGun} gün · veri kalitesi`}
        />
        <Kutu
          etiket="Düzeltilen"
          deger={`%${veri.duzeltilenOran}`}
          alt={`${veri.duzeltilenGun} gün · şef bu ekranı kullanıyor mu`}
          uyari={veri.duzeltilenOran > 10}
        />
        <Kutu
          etiket="Otomatik kapanan"
          deger={`%${veri.otomatikKapanmaOrani}`}
          alt={`${veri.otomatikKapanan} gün · eşik %5`}
          uyari={veri.otomatikKapanmaOrani > 5}
        />
        {/*
          ÖN İZİNLİ GÜNDE OTOMATİK KAPANMA sıfıra yakın olmalı: o gün mesai
          onaylıydı ama çıkış vardiya bitişine yazıldı; fiili fazla çalışma
          SESSİZCE kayboldu. Sıfırdan büyükse ücret kaybı var demektir.
        */}
        <Kutu
          etiket="Ön izinli otom. kapanma"
          deger={String(veri.onIzinliOtomatikKapanan)}
          alt="sessiz ücret kaybı · hedef 0"
          uyari={veri.onIzinliOtomatikKapanan > 0}
        />
      </div>

      {gerekceler.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-slate-600">
            Düzeltme gerekçeleri — hangi sorunun çözüleceğini bu dağılım söyler
          </p>
          <div className="flex flex-wrap gap-2">
            {gerekceler.map(([kod, adet]) => (
              <span
                key={kod}
                className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                title={GEREKCE_ACIKLAMA[kod] ?? ''}
              >
                {GEREKCE_ETIKET[kod] ?? kod}: {adet}
              </span>
            ))}
          </div>
        </div>
      )}

      {veri.puantorBasimi > 0 && (
        <p className="text-xs text-slate-500">
          Puantör basımı: {veri.puantorBasimi} · başkası adına basılan kayıtlar denetim izinde
          işaretli.
        </p>
      )}
    </div>
  );
}

const GEREKCE_ETIKET: Record<string, string> = {
  unuttu: 'Unuttu',
  telefon_arizasi: 'Telefon arızası',
  internet_yok: 'İnternet yok',
  vardiya_uzadi: 'Vardiya uzadı',
  hatali_basma: 'Hatalı basma',
  diger: 'Diğer',
};

const GEREKCE_ACIKLAMA: Record<string, string> = {
  unuttu: 'Alışkanlık ve eğitim sorunu',
  telefon_arizasi: 'Cihaz ve altyapı sorunu — telefon politikası işlemiyor olabilir',
  internet_yok: 'Altyapı sorunu',
  vardiya_uzadi: 'Plan gerçeği yansıtmıyor',
  hatali_basma: 'Arayüz sorunu',
};

function Kutu({
  etiket,
  deger,
  alt,
  uyari = false,
}: {
  etiket: string;
  deger: string;
  alt: string;
  uyari?: boolean;
}) {
  return (
    <div
      className={`rounded-md border p-3 ${
        uyari ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <p className="text-xs text-slate-600">{etiket}</p>
      <p className={`mt-0.5 text-xl font-semibold ${uyari ? 'text-amber-900' : 'text-slate-900'}`}>
        {deger}
      </p>
      <p className="mt-0.5 text-[11px] text-slate-500">{alt}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Denetim tablosu                                                     */
/* ------------------------------------------------------------------ */

const ISARET_ETIKET: Record<string, string> = {
  otomatik_kapanma: 'otomatik kapandı',
  on_izinli_otomatik_kapanma: 'ön izinli otom. kapandı',
  puantor_basimi: 'puantör bastı',
  sef_girisi: 'şef girdi',
  gec_kalma: 'geç kalma',
  erken_cikis: 'erken çıkış',
  basma_yok: 'basma yok',
};

function DenetimTablosu({ satirlar, bekliyor }: { satirlar: DenetimSatiri[]; bekliyor: boolean }) {
  if (bekliyor) return <p className="text-sm text-slate-500">Yükleniyor…</p>;

  if (satirlar.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        Bu aralıkta bakılması gereken gün yok.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Tarih</th>
            <th className="px-3 py-2 font-medium">Personel</th>
            <th className="px-3 py-2 font-medium">Birim</th>
            <th className="px-3 py-2 font-medium">Basılan</th>
            <th className="px-3 py-2 font-medium">Geçerli</th>
            <th className="px-3 py-2 font-medium">Net</th>
            <th className="px-3 py-2 font-medium">Durum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {satirlar.map((s) => (
            <tr key={`${s.atamaId}-${s.tarih}`} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-3 py-2 text-slate-700">{tarihMetni(s.tarih)}</td>
              <td className="px-3 py-2">
                {/*
                  ADA TIKLAYINCA KİŞİ ÇİZELGESİ AÇILIR. Bu ekran yalnız
                  İŞARETLİ günleri gösteriyor; şefin "bu kişinin ayı nasıl
                  geçmiş" sorusunu cevaplaması için tam çizelgeye geçmesi
                  gerekiyor. Bağ olmadan iki ekran arasında gezinilemiyordu.
                */}
                <Link to={`/puantaj/${s.atamaId}`} className="block text-kurum-700 hover:underline">
                  {s.adSoyad}
                </Link>
                <span className="block font-mono text-[10px] text-slate-400">{s.personelNo}</span>
              </td>
              <td className="px-3 py-2 text-slate-600">{s.birimAd}</td>

              {/*
                BASILAN VE GEÇERLİ AYRI SÜTUNDA. Yuvarlama sonrası saat tek
                başına gösterilseydi personel "ben 07:58'de bastım" derken
                ekranda 08:00 görünür ve itiraz konuşulamazdı.
              */}
              <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                {saatMetni(s.basilanIlk)} – {saatMetni(s.basilanSon)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                {saatMetni(s.ilkGiris)} – {saatMetni(s.sonCikis)}
                {s.girisYuvarlamaDk !== null && s.girisYuvarlamaDk !== 0 && (
                  <span className="ml-1 text-[10px] text-slate-400">
                    ({s.girisYuvarlamaDk > 0 ? '+' : ''}
                    {s.girisYuvarlamaDk}d)
                  </span>
                )}
              </td>

              <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                {sureMetni(s.netDakika)}
              </td>

              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {s.devamsizMi && (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800">
                      devamsız
                    </span>
                  )}
                  {s.onIzinsizAcil && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                      ön izinsiz acil
                    </span>
                  )}
                  {s.duzeltildiMi && (
                    <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs text-sky-800">
                      düzeltildi
                    </span>
                  )}
                  {s.isaretKodlari.map((kod) => (
                    <span
                      key={kod}
                      className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                    >
                      {ISARET_ETIKET[kod] ?? kod}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Kota tablosu                                                        */
/* ------------------------------------------------------------------ */

const KOTA_BICIMI: Record<string, string> = {
  normal: 'bg-emerald-100 text-emerald-800',
  uyari: 'bg-amber-100 text-amber-900',
  kritik: 'bg-orange-100 text-orange-900',
  doldu: 'bg-red-100 text-red-800',
};

function KotaTablosu({ satirlar, bekliyor }: { satirlar: KotaDurumu[]; bekliyor: boolean }) {
  if (bekliyor) return <p className="text-sm text-slate-500">Yükleniyor…</p>;

  if (satirlar.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
        Kota kaydı yok.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-2">
        <p className="text-xs text-slate-500">
          4857/41 · yıllık 270 saat. Ön izinsiz acil mesai DE kotayı tüketir — sınır fiili çalışmaya
          bağlıdır, izne değil. <strong>doldu</strong> durumunda mesai ön izni bloklanır.
        </p>
      </div>

      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Personel</th>
            <th className="px-3 py-2 font-medium">Birim</th>
            <th className="px-3 py-2 font-medium">Kullanılan</th>
            <th className="px-3 py-2 font-medium">Kalan</th>
            <th className="px-3 py-2 font-medium">Ön izinsiz</th>
            <th className="px-3 py-2 font-medium">Durum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {satirlar.map((s) => (
            <tr key={s.atamaId} className="hover:bg-slate-50">
              <td className="px-3 py-2">
                <Link to={`/puantaj/${s.atamaId}`} className="block text-kurum-700 hover:underline">
                  {s.adSoyad}
                </Link>
                <span className="block font-mono text-[10px] text-slate-400">{s.personelNo}</span>
              </td>
              <td className="px-3 py-2 text-slate-600">{s.birimAd}</td>
              {/* Saatler METİN olarak basılıyor; ondalık kaybı olmamalı. */}
              <td className="px-3 py-2 text-slate-700">
                {s.kullanilanSaat} / {s.limitSaat}
                {s.kullanimOrani !== null && (
                  <span className="ml-1 text-xs text-slate-400">(%{s.kullanimOrani})</span>
                )}
              </td>
              <td className="px-3 py-2 text-slate-700">{s.kalanSaat}</td>
              <td className="px-3 py-2 text-slate-700">{s.onIzinsizSaat}</td>
              <td className="px-3 py-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-xs ${KOTA_BICIMI[s.kotaDurum] ?? 'bg-slate-100 text-slate-600'}`}
                >
                  {s.kotaDurum}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
